import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import AttachmentIcon from '@mui/icons-material/Attachment';
import { Box, Button, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { DataGridPremium as DataGrid, GridColDef } from '@mui/x-data-grid-premium';
import {
  Document_UploadFileMutation,
  Estate,
  EstateInputDataInstance,
  EstateInputDataInstance_FieldsFragment,
  EstateWorkItem_GetEstateWorkItemsByPortalIdDocument,
  useEstateInputDataInstance_ResetEstateInputDataInstanceMutation,
} from 'queries';

import DocumentTagPill from 'pages/common/document/document-tag-pill';
import DocumentUploadModal from 'pages/common/document/document-upload-modal';
import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';
const useStyles = makeStyles(() => ({
  container: {
    marginLeft: '20px',
  },
  columnHeader: {
    width: '200px',
  },
  column: {
    display: 'flex',
    width: '100%',
    padding: '20px 0',
    borderBottom: '1px solid #ECEEF1',
    alignItems: 'center',
  },
  dataGrid: {
    border: 0,
    borderRadius: '8px',
    '&.MuiDataGrid-root .MuiDataGrid-cell, &.MuiDataGrid-root .MuiDataGrid-columnHeader': {
      '&:focus, &:focus-within': {
        outline: 'none',
      },
    },
    '& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader': {
      display: 'none',
    },
    '& .MuiDataGrid-virtualScroller': {
      marginTop: '0 !important',
    },
    '& .MuiDataGrid-row': {
      borderBottom: '1px solid #ECEEF1',
      borderTop: 'none',
    },
    '& .MuiDataGrid-cell': {
      borderBottom: 'none',
      borderTop: 'none',
      paddingLeft: '0px',
    },
  },
  documentAttachment: {
    display: 'flex',
    flexDirection: 'row',
    gap: '5px',
    color: '#2A70D8',
  },
  documentTitle: {
    display: 'flex',
    alignItems: 'center',
  },
  documentSubtitle: {
    fontSize: '14px',
    marginRight: '8px',
  },
  title: {
    fontSize: '32px',
    fontWeight: '700',
    marginTop: '24px',
  },
  removeButton: {
    backgroundColor: '#FFF4F4',
    color: '#DB5461',
    borderRadius: '24px',
    boxShadow: 'none',
    fontSize: '12px',
    px: '16px',
    width: '120px',
    marginLeft: '5px',
    '&:hover': {
      backgroundColor: 'inherit',
    },
    '&:active': {
      backgroundColor: 'inherit',
    },
  },
  subtitle: {
    fontSize: '14px',
    marginTop: '12px',
  },
  uploadButton: {
    backgroundColor: '#1C2541',
    color: '#FFFFFF',
    borderRadius: '24px',
    boxShadow: 'none',
    fontSize: '12px',
    px: '16px',
    width: '120px',
    marginLeft: '5px',
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: '700',
  },
}));

type Props = {
  estate: Estate;
  estateInputDataInstances: EstateInputDataInstance_FieldsFragment[];
  refetchEstateInputDataInstances: Function;
};

const AssignedTaskMatterInfoRequiredDocsForm = ({
  estate,
  estateInputDataInstances,
  refetchEstateInputDataInstances,
}: Props) => {
  const classes = useStyles();
  const { portalId } = useParams<Record<string, string>>();
  const [estateInputDataInstanceToUpload, setEstateInputDataInstanceToUpload] =
    useState<EstateInputDataInstance | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);

  const [resetEstateInputDataInstance] =
    useEstateInputDataInstance_ResetEstateInputDataInstanceMutation({
      refetchQueries: [
        {
          query: EstateWorkItem_GetEstateWorkItemsByPortalIdDocument,
          variables: { portalId },
        },
      ],
      onCompleted: () => {
        showToast({
          message: 'Document removed successfully.',
          severity: NotificationSeverity.SUCCESS,
        });
      },
      onError: () => {
        showAlert({
          message: 'An error occurred removing your document.',
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  const handleFileUploadClick = (estateInputDataInstance: EstateInputDataInstance) => {
    setEstateInputDataInstanceToUpload(estateInputDataInstance);
    setShowUploadModal(true);
  };

  const handleFileRemoveClick = async (estateInputDataInstance: EstateInputDataInstance) => {
    if (!window.confirm('Are you sure you want to remove this file?')) {
      return;
    }
    await resetEstateInputDataInstance({
      variables: {
        input: {
          portalId,
          id: estateInputDataInstance.id,
        },
      },
    });
  };

  const handleUploadComplete = async (_: Document_UploadFileMutation) => {
    showToast({
      message: 'Document uploaded successfully',
      severity: NotificationSeverity.SUCCESS,
    });
    setEstateInputDataInstanceToUpload(null);
    setShowUploadModal(false);
    refetchEstateInputDataInstances();
  };

  const getTableColumns = useCallback(() => {
    const columns: GridColDef[] = [
      {
        flex: 2,
        field: 'filename',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const estateInputDataInstance = params.row as EstateInputDataInstance;
          const documentName = estateInputDataInstance.document?.name;
          return (
            <Box className={classes.documentTitle}>
              <div className={classes.documentAttachment}>
                <AttachmentIcon fontSize="small" />
                <Typography className={classes.documentSubtitle}>
                  <em>{documentName || estateInputDataInstance.estateInputDataFormat?.name}.pdf</em>
                </Typography>
              </div>
            </Box>
          );
        },
      },
      {
        flex: 2,
        field: 'documentTag',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const estateInputDataInstance = params.row as EstateInputDataInstance;
          return (
            <div>
              <DocumentTagPill
                documentTag={estateInputDataInstance.estateInputDataFormat.documentTag}
              />
            </div>
          );
        },
      },
      {
        flex: 1,
        field: 'remove',
        renderCell: (params) => {
          const estateInputDataInstance = params.row as EstateInputDataInstance;
          return (
            <div style={{ display: 'flex' }}>
              {estateInputDataInstance.data ? (
                <Button
                  className={classes.removeButton}
                  component="span"
                  variant="contained"
                  size="small"
                  onClick={() => {
                    handleFileRemoveClick(estateInputDataInstance);
                  }}
                >
                  <Typography>Remove</Typography>
                </Button>
              ) : (
                <Button
                  className={classes.uploadButton}
                  component="span"
                  variant="contained"
                  size="small"
                  onClick={() => {
                    handleFileUploadClick(estateInputDataInstance);
                  }}
                >
                  <Typography>Upload</Typography>
                </Button>
              )}
            </div>
          );
        },
      },
    ];

    return columns;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetchEstateInputDataInstances, classes]);

  return (
    <>
      <Box className={classes.container}>
        <Typography className={classes.sectionTitle}>Required Documents</Typography>
        <DataGrid
          className={classes.dataGrid}
          columns={getTableColumns()}
          rows={estateInputDataInstances}
          disableColumnReorder
          disableRowSelectionOnClick
          disableColumnMenu
          hideFooter
          columnHeaderHeight={0}
          autoHeight={true}
        />
        {estateInputDataInstanceToUpload && (
          <DocumentUploadModal
            documentTagId={estateInputDataInstanceToUpload.estateInputDataFormat?.documentTagId!}
            title={'Upload ' + estateInputDataInstanceToUpload.estateInputDataFormat?.name}
            showModal={showUploadModal}
            setShowModal={setShowUploadModal}
            portalId={portalId}
            onExitClick={() => setEstateInputDataInstanceToUpload(null)}
            onUploadComplete={handleUploadComplete}
            parentId={estate.documentsFolderId!}
            estateWorkItemId={estateInputDataInstanceToUpload.estateWorkItemId!}
            estateInputDataInstanceId={estateInputDataInstanceToUpload.id!}
          />
        )}
      </Box>
    </>
  );
};

export default AssignedTaskMatterInfoRequiredDocsForm;
